import {PER_URL_ANALYSIS_COST} from '../../utils/analysisCost';

interface ManageablePrompt {
    id: string;
    userId?: string;
    companyId?: string;
}

interface ManagingUser {
    id: string;
    role: 'user' | 'eskimi_admin' | 'adops' | 'admin';
}

/** Row-level edit/delete gating: owner, or admin/eskimi_admin/adops. */
export function canManage(prompt: ManageablePrompt, user: ManagingUser): boolean {
    return Boolean(
        (prompt.userId && prompt.userId === user.id) ||
        user.role === 'admin' ||
        user.role === 'eskimi_admin' ||
        user.role === 'adops',
    );
}

/** Default (system) blueprints have no companyId; only role 'admin' may delete them. */
export function isDeleteDisabled(
    prompt: {id: string; companyId?: string},
    user: {role: ManagingUser['role']} | null | undefined,
): boolean {
    if (user?.role === 'admin') return false;
    return !prompt.companyId;
}

interface ConnectableJob {
    promptIds: string[];
}

/** Jobs whose promptIds include this blueprint's id. */
export function connectedJobsFor<T extends ConnectableJob>(jobs: T[], promptId: string): T[] {
    return jobs.filter(j => j.promptIds?.includes(promptId));
}

interface CostedJob {
    totalUrlsProcessed?: number | null;
}

/**
 * Estimated total DeepContext scan cost for a blueprint (bd-deepcontext-jvqwp):
 * SUM(urls_processed across all runs of the job(s) using this blueprint) ×
 * PER_URL_ANALYSIS_COST. Returns null when the blueprint has no job runs at
 * all (no connected jobs, or none have ever run) so the column renders a
 * muted em dash instead of $0.00.
 */
export function estimateScanCost(jobs: CostedJob[]): number | null {
    let hasRuns = false;
    let totalUrls = 0;
    for (const job of jobs) {
        if (job.totalUrlsProcessed != null) {
            hasRuns = true;
            totalUrls += job.totalUrlsProcessed;
        }
    }
    return hasRuns ? totalUrls * PER_URL_ANALYSIS_COST : null;
}
